import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Check } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import FAQ, { faqSchema } from "@/components/FAQ";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import { ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import api from "@/lib/api";

export default function IndustryDetail() {
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/industries/${slug}`).then((r) => setIndustry(r.data))
      .catch(() => setIndustry(null)).finally(() => setLoading(false));
  }, [slug]);

  if (!loading && !industry) return <NotFound />;
  if (!industry) return <div className="min-h-[60vh]" />;

  const crumbs = [{ label: "Industries", to: "/industries" }, { label: industry.name }];
  const jsonLd = [breadcrumbSchema(crumbs), ...(industry.faqs?.length ? [faqSchema(industry.faqs)] : [])];

  return (
    <div data-testid="industry-detail-page">
      <SEO title={`${industry.name} — Cleanroom Equipment & Solutions`} path={`/industries/${slug}`}
        description={industry.intro} image={industry.image} jsonLd={jsonLd} />

      <section className="blueprint-dark relative overflow-hidden bg-ink">
        <img src={industry.image} alt="" aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Breadcrumbs dark items={crumbs} />
          <p className="eyebrow eyebrow-light">Industry</p>
          <KineticLines lines={[industry.name]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{industry.intro}</p>
          <Link to={`/request-a-quote?application=${encodeURIComponent(industry.name)}`} data-testid="industry-rfq-button"
            className="group mt-8 inline-flex items-center gap-2 bg-accent px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
            Request a Quote <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-28">
        <div className="lg:col-span-6">
          <SectionHead eyebrow="Challenges" title="What This Industry Demands" />
          <ul className="mt-8 space-y-4">
            {industry.challenges?.map((c, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex items-start gap-3 border border-line bg-white p-4 text-sm text-ink" data-testid={`challenge-item-${i}`}>
                  <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" /> {c}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          <SectionHead eyebrow="STJ Solutions" title="How STJ Responds" />
          <ul className="mt-8 space-y-4">
            {industry.solutions?.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex items-start gap-3 border border-line bg-mist p-4 text-sm text-ink" data-testid={`solution-item-${i}`}>
                  <Check size={15} className="mt-0.5 shrink-0 text-accent" /> {s}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {industry.applications?.length > 0 && (
        <section className="border-y border-line bg-mist">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Applications" title="Where Our Equipment Works" />
            <div className="mt-8 flex flex-wrap gap-2.5">
              {industry.applications.map((a, i) => (
                <span key={i} data-testid={`application-chip-${i}`} className="border border-line bg-white px-4 py-2 text-sm text-ink">{a}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {industry.related_products?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Equipment" title="Applicable Products" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="industry-products-grid">
            {industry.related_products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </section>
      )}

      {industry.faqs?.length > 0 && (
        <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Questions" title="Frequently Asked Questions" />
          <div className="mt-10"><FAQ items={industry.faqs} /></div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
